import { signal, untracked } from '@angular/core';

/** Vangnet (spec 2026-10-06 §2.5): ouder dan dit → een bezoek ververst stil op de achtergrond. */
export const HD_CACHE_MAX_AGE_MS = 10 * 60 * 1000;

/** Publieke, reactieve toestand van één cache-sleutel. */
export interface HdCacheEntry<T> {
  readonly value: T | undefined;
  /** Moment (ms) van het laatst opgehaalde antwoord of de laatste lokale patch. */
  readonly loadedAt: number | undefined;
  readonly loading: boolean;
  readonly error: string | undefined;
  /** Mogelijk achterhaald (mutatie of seintje): het volgende bezoek ververst stil. */
  readonly stale: boolean;
}

interface HdCacheInternal {
  /** Telt elke mutatie (invalidate/set); een load van vóór de laatste telt als achterhaald. */
  generation: number;
  /** Aantal kijkers (panelen die de sleutel nu tonen); bekeken entries verversen meteen. */
  watchers: number;
  inflight: Promise<void> | undefined;
}

export interface HdKeyedCacheOptions {
  maxAgeMs?: number;
  /** Testbare klok. */
  now?: () => number;
}

/**
 * Sessiecache per sleutel voor serverdata (spec chart-datacache 2026-10-06 §3.1).
 *
 * - `watch(key)` registreert een kijker en laadt: koud als er niets is, stil op de achtergrond als de
 *   entry oud is (`stale` of ouder dan het vangnet). De bestaande waarde blijft altijd zichtbaar.
 * - Eén load per sleutel tegelijk: een tweede vraag hergebruikt de lopende.
 * - `invalidate` markeert entries als oud; bekeken entries herladen meteen, onbekeken pas bij het
 *   volgende bezoek.
 *
 * BELANGRIJK (generatieteller): een antwoord dat vertrok vóór de laatste mutatie mag een nieuwere
 * waarde nooit overschrijven. Zonder die teller zou bv. "notitie opslaan, dan meteen een bijlage
 * toevoegen" de bijlage kunnen laten verdwijnen omdat de eerste herlaad later terugkomt dan de tweede
 * mutatie. Zie hd-keyed-cache.spec.ts ("mutatie tijdens een lopende load").
 */
export class HdKeyedCache<T> {
  private readonly entries = signal<ReadonlyMap<string, HdCacheEntry<T>>>(new Map());
  private readonly internals = new Map<string, HdCacheInternal>();
  private readonly maxAgeMs: number;
  private readonly now: () => number;

  constructor(
    private readonly loader: (key: string) => Promise<T>,
    options: HdKeyedCacheOptions = {},
  ) {
    this.maxAgeMs = options.maxAgeMs ?? HD_CACHE_MAX_AGE_MS;
    this.now = options.now ?? (() => Date.now());
  }

  /** Reactief: leest de entries-signal (gebruik in `computed`/templates). */
  entry(key: string | null | undefined): HdCacheEntry<T> | undefined {
    if (!key) return undefined;
    return this.entries().get(key);
  }

  /** Reactief: de gecachte waarde, of undefined. */
  value(key: string | null | undefined): T | undefined {
    return this.entry(key)?.value;
  }

  /** Reactief: alle entries (voor afgeleide toestand zoals "laadt er iets voor deze chart"). */
  all(): ReadonlyMap<string, HdCacheEntry<T>> {
    return this.entries();
  }

  /**
   * Registreert een kijker en zorgt dat de data er (vers genoeg) is. Geeft een release-functie terug,
   * bedoeld voor `onCleanup` in een effect. Leest de signal zelf niet getrackt, zodat het effect enkel
   * op zijn eigen sleutel reageert.
   */
  watch(key: string): () => void {
    const internal = this.internal(key);
    internal.watchers++;
    untracked(() => this.ensure(key));
    let released = false;
    return () => {
      if (released) return;
      released = true;
      internal.watchers = Math.max(0, internal.watchers - 1);
    };
  }

  /** Haalt altijd vers op bij de server (verversknop); wacht tot het antwoord binnen is. */
  async refresh(key: string): Promise<void> {
    const internal = this.internal(key);
    internal.generation++;
    this.patch(key, { stale: true });
    if (internal.inflight) await internal.inflight;
    if (!internal.inflight && this.peek(key)?.stale !== false) this.load(key);
    if (internal.inflight) await internal.inflight;
  }

  /** Lokale patch met een vers antwoord (bv. de DTO die een mutatie teruggaf). */
  set(key: string, value: T): void {
    this.internal(key).generation++;
    this.patch(key, { value, loadedAt: this.now(), error: undefined, stale: false });
  }

  /** Lokale patch op de bestaande waarde; no-op zonder waarde (dan valt er niets bij te werken). */
  update(key: string, fn: (current: T) => T): void {
    const current = this.peek(key)?.value;
    if (current === undefined) return;
    this.set(key, fn(current));
  }

  /** Markeert de entries waarvoor het predicaat klopt als oud; bekeken entries herladen meteen. */
  invalidate(predicate: (key: string, value: T | undefined) => boolean): void {
    const snapshot = untracked(() => this.entries());
    for (const [key, entry] of snapshot) {
      if (!predicate(key, entry.value)) continue;
      const internal = this.internal(key);
      internal.generation++;
      this.patch(key, { stale: true });
      if (internal.watchers > 0 && !internal.inflight) this.load(key);
    }
  }

  /** Na een herverbinding: alles kan gemist zijn. */
  markAllStale(): void {
    this.invalidate(() => true);
  }

  private ensure(key: string): void {
    const internal = this.internal(key);
    if (internal.inflight) return;
    const entry = this.peek(key);
    if (entry?.value === undefined) {
      this.load(key);
      return;
    }
    const age = entry.loadedAt === undefined ? Number.POSITIVE_INFINITY : this.now() - entry.loadedAt;
    if (entry.stale || age > this.maxAgeMs) this.load(key);
  }

  private load(key: string): void {
    const internal = this.internal(key);
    const generation = internal.generation;
    this.patch(key, { loading: true, error: undefined });
    internal.inflight = this.loader(key).then(
      value => {
        internal.inflight = undefined;
        const outdated = internal.generation !== generation;
        if (!outdated) {
          this.patch(key, { value, loadedAt: this.now(), loading: false, error: undefined, stale: false });
        } else if (this.peek(key)?.value === undefined) {
          // Achterhaald maar beter dan niets: tonen, en als oud laten staan.
          this.patch(key, { value, loadedAt: this.now(), loading: false, error: undefined, stale: true });
        } else {
          this.patch(key, { loading: false });
        }
        if (this.peek(key)?.stale && internal.watchers > 0) this.load(key);
      },
      (error: unknown) => {
        internal.inflight = undefined;
        this.patch(key, { loading: false, error: error instanceof Error ? error.message : `${error}` });
      },
    );
  }

  private peek(key: string): HdCacheEntry<T> | undefined {
    return untracked(() => this.entries()).get(key);
  }

  private patch(key: string, changes: Partial<HdCacheEntry<T>>): void {
    const current = this.peek(key) ?? { value: undefined, loadedAt: undefined, loading: false, error: undefined, stale: false };
    this.entries.update(map => new Map(map).set(key, { ...current, ...changes }));
  }

  private internal(key: string): HdCacheInternal {
    let internal = this.internals.get(key);
    if (!internal) {
      internal = { generation: 0, watchers: 0, inflight: undefined };
      this.internals.set(key, internal);
    }
    return internal;
  }
}
